import React, { useState, useEffect } from 'react';
import { facilityAPI } from '../services/api';
import api from '../services/api';
import { AlertTriangle, CheckCircle, Clock, Download, Play, RefreshCw, Plus, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ScoreGauge({ score, label, color }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(score, 100) / 100;
  const dash = pct * circ;
  const gap = circ - dash;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={r} fill="none" stroke="#F1F5F9" strokeWidth="14" />
        <circle cx="80" cy="80" r={r} fill="none" stroke={color} strokeWidth="14"
          strokeDasharray={`${dash} ${gap}`}
          strokeLinecap="round"
          transform="rotate(-90 80 80)"
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
        <text x="80" y="72" textAnchor="middle" style={{ fontFamily: 'Manrope', fontSize: 28, fontWeight: 800, fill: '#0A1628' }}>{score}%</text>
        <text x="80" y="92" textAnchor="middle" style={{ fontFamily: 'DM Sans', fontSize: 10, fontWeight: 600, fill: '#6B7280' }}>READINESS</text>
      </svg>
      <div style={{ fontFamily: 'var(--font-heading)', fontSize: 14, fontWeight: 800, color, marginTop: 4 }}>{label}</div>
    </div>
  );
}

function EmissionBar({ label, source, pct, unit, actual, limit, permitCondition }) {
  const color = pct === null ? '#CBD5E1' : pct >= 100 ? '#DC2626' : pct >= 80 ? '#D97706' : '#16A34A';
  const displayPct = pct !== null ? Math.min(pct, 100) : 0;

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: '#0A1628' }}>{label}</span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>{source}</span>
          {permitCondition && <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>§{permitCondition}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#374151' }}>
            {pct !== null ? `${actual} / ${limit} ${unit}` : 'No data'}
          </span>
          <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color, minWidth: 36, textAlign: 'right' }}>
            {pct !== null ? `${pct.toFixed(0)}%` : '—'}
          </span>
        </div>
      </div>
      <div style={{ height: 8, background: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${displayPct}%`, background: color, borderRadius: 4, transition: 'width 0.8s ease' }} />
      </div>
    </div>
  );
}

function DonutChart({ completed, pending, overdue }) {
  const total = completed + pending + overdue || 1;
  const size = 120;
  const r = 44;
  const circ = 2 * Math.PI * r;

  const completedPct = completed / total;
  const pendingPct = pending / total;
  const overduePct = overdue / total;

  const completedDash = completedPct * circ;
  const pendingDash = pendingPct * circ;
  const overdueDash = overduePct * circ;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F1F5F9" strokeWidth="12" />
        {overdue > 0 && (
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#DC2626" strokeWidth="12"
            strokeDasharray={`${overdueDash} ${circ - overdueDash}`}
            strokeLinecap="butt"
            transform={`rotate(-90 ${size/2} ${size/2})`} />
        )}
        {pending > 0 && (
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#D97706" strokeWidth="12"
            strokeDasharray={`${pendingDash} ${circ - pendingDash}`}
            strokeLinecap="butt"
            transform={`rotate(${-90 + overduePct * 360} ${size/2} ${size/2})`} />
        )}
        {completed > 0 && (
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#16A34A" strokeWidth="12"
            strokeDasharray={`${completedDash} ${circ - completedDash}`}
            strokeLinecap="butt"
            transform={`rotate(${-90 + (overduePct + pendingPct) * 360} ${size/2} ${size/2})`} />
        )}
        <text x={size/2} y={size/2 - 6} textAnchor="middle" style={{ fontFamily: 'Manrope', fontSize: 22, fontWeight: 800, fill: '#0A1628' }}>{total}</text>
        <text x={size/2} y={size/2 + 10} textAnchor="middle" style={{ fontFamily: 'DM Sans', fontSize: 9, fill: '#6B7280' }}>TASKS</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {[
          { color: '#16A34A', label: 'Completed', count: completed },
          { color: '#D97706', label: 'Pending', count: pending },
          { color: '#DC2626', label: 'Overdue', count: overdue },
        ].map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#374151' }}>{item.count} {item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SEVERITY_CONFIG = {
  critical: { icon: AlertTriangle, color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', label: 'CRITICAL' },
  warning: { icon: Clock, color: '#D97706', bg: '#FFFBEB', border: '#FDE68A', label: 'WARNING' },
  pass: { icon: CheckCircle, color: '#16A34A', bg: '#F0FDF4', border: '#BBF7D0', label: 'PASS' },
};

function Audit() {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [showPassingItems, setShowPassingItems] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  useEffect(() => {
    facilityAPI.getAll().then(res => {
      const facs = res.data.facilities || [];
      setFacilities(facs);
      if (facs.length > 0) setSelectedFacility(facs[0].id.toString());
    });
  }, []);

  const runAudit = async () => {
    if (!selectedFacility) return toast.error('Please select a facility');
    setLoading(true);
    try {
      const res = await api.get(`/audit/run/${selectedFacility}`);
      setAuditData(res.data);
      setLastRun(new Date());
    } catch (err) {
      toast.error('Failed to run audit');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    if (!selectedFacility) return;
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/audit/report/${selectedFacility}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const facility = facilities.find(f => f.id === parseInt(selectedFacility));
      link.setAttribute('download', `inspection-readiness-${facility?.name || 'facility'}-${Date.now()}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded!');
    } catch (err) {
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const createTasksFromFindings = async () => {
    if (!auditData) return;
    setCreatingTasks(true);
    try {
      const criticalFindings = auditData.findings.filter(f => f.severity === 'critical');
      const res = await api.post(`/audit/create-tasks/${selectedFacility}`, { findings: criticalFindings });
      toast.success(`${res.data.created} tasks created from audit findings!`);
    } catch (err) {
      toast.error('Failed to create tasks');
    } finally {
      setCreatingTasks(false);
    }
  };

  const criticalCount = auditData?.findings?.filter(f => f.severity === 'critical').length || 0;
  const warningCount = auditData?.findings?.filter(f => f.severity === 'warning').length || 0;
  const passingCount = auditData?.findings?.filter(f => f.severity === 'pass').length || 0;

  const visibleFindings = auditData?.findings?.filter(f => showPassingItems || f.severity !== 'pass') || [];

  return (
    <div>
      {/* Header */}
      <div className='audit-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div className="page-title">Audit</div>
          <div className="page-subtitle">Compliance Check & Gap Analysis</div>
        </div>
        <div className='audit-header-btns' style={{ display: 'flex', gap: 10 }}>
          {auditData && (
            <>
              <button onClick={createTasksFromFindings} disabled={creatingTasks || criticalCount === 0} className="btn btn-ghost" style={{ fontSize: 13 }}>
                <Plus size={14} /> {creatingTasks ? 'Creating...' : `Create Tasks (${criticalCount})`}
              </button>
              <button onClick={downloadReport} disabled={downloading} className="btn btn-ghost" style={{ fontSize: 13 }}>
                <Download size={14} /> {downloading ? 'Generating...' : 'Download Report'}
              </button>
            </>
          )}
          <button onClick={runAudit} disabled={loading || !selectedFacility} className="btn btn-primary">
            {loading ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Running...</> : <><Play size={14} /> Run Audit</>}
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 14, color: '#92400E', fontFamily: 'var(--font-body)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        This is an AI-assisted compliance readiness tool for internal use only. It does not constitute a formal regulatory compliance audit. Always review with a qualified environmental professional before regulatory submissions.
      </div>

      {/* Facility + last run */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <select value={selectedFacility} onChange={e => { setSelectedFacility(e.target.value); setAuditData(null); }} style={{ maxWidth: 300, minWidth: 200, flex: 1 }}>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.name || f.facility_name}</option>)}
        </select>
        {lastRun && <span style={{ fontSize: 14, color: '#94a3b8', fontFamily: 'var(--font-body)' }}>Last run: {lastRun.toLocaleTimeString()}</span>}
      </div>

      {/* Empty state */}
      {!auditData && !loading && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <FileText size={48} color="#CBD5E1" style={{ margin: '0 auto 16px', display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: '#0A1628', marginBottom: 8 }}>Ready to audit</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#6B7280', marginBottom: 24 }}>Select a facility and click Run Audit to check compliance status and identify gaps</div>
          <button onClick={runAudit} disabled={!selectedFacility} className="btn btn-primary">
            <Play size={15} /> Run Audit
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ width: 48, height: 48, border: '4px solid #E2E8F0', borderTopColor: '#0066FF', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700, color: '#0A1628', marginBottom: 6 }}>Analyzing compliance...</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#6B7280' }}>Checking emission limits, tasks, records, and permit status</div>
        </div>
      )}

      {/* Audit Results */}
      {auditData && !loading && (
        <div>
          {/* Score row */}
          <div className='audit-score-row' style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 16, marginBottom: 20 }}>

            {/* Score gauge */}
            <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px' }}>
              <ScoreGauge score={auditData.score} label={auditData.scoreLabel} color={auditData.scoreColor} />
            </div>

            {/* Summary stats */}
            <div className='audit-stats-row' style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { label: 'Critical', count: criticalCount, color: '#DC2626', bg: '#FEF2F2', icon: AlertTriangle },
                { label: 'Warnings', count: warningCount, color: '#D97706', bg: '#FFFBEB', icon: Clock },
                { label: 'Passing', count: passingCount, color: '#16A34A', bg: '#F0FDF4', icon: CheckCircle },
              ].map(item => (
                <div key={item.label} className="stat-card" style={{ background: item.bg, borderColor: 'transparent' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <item.icon size={14} color={item.color} />
                  </div>
                  <div>
                    <div className="stat-number" style={{ color: item.color }}>{item.count}</div>
                    <div className="stat-label">{item.label}</div>
                  </div>
                </div>
              ))}

              {/* Task donut */}
              <div className="card" className='audit-donut-card' style={{ gridColumn: 'span 3', padding: '12px 16px' }}>
                <div className="section-label" style={{ marginBottom: 12 }}>Task Completion</div>
                <DonutChart
                  completed={auditData.taskStats?.completed || 0}
                  pending={auditData.taskStats?.pending || 0}
                  overdue={auditData.taskStats?.overdue || 0}
                />
              </div>
            </div>
          </div>

          {/* Emissions compliance bars */}
          {auditData.emissionFindings?.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="section-label" style={{ marginBottom: 16 }}>Emission Limits — Rolling 12-Month</div>
              {auditData.emissionFindings.map((ef, i) => (
                <EmissionBar key={i}
                  label={ef.parameter}
                  source={ef.source}
                  pct={ef.pct}
                  unit={ef.unit}
                  actual={ef.actual}
                  limit={ef.limit}
                  permitCondition={ef.permitCondition}
                />
              ))}
            </div>
          )}

          {/* Findings list */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="section-label" style={{ margin: 0 }}>Findings & Gap Analysis</div>
              <button onClick={() => setShowPassingItems(!showPassingItems)} className="btn btn-ghost" style={{ fontSize: 14, padding: '5px 12px' }}>
                {showPassingItems ? 'Hide passing' : 'Show all'}
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visibleFindings.map((finding, i) => {
                const config = SEVERITY_CONFIG[finding.severity];
                const Icon = config.icon;
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 12px', background: config.bg, border: `1px solid ${config.border}`, borderRadius: 9 }}>
                    <Icon size={15} color={config.color} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700, color: config.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{config.label}</span>
                        <span style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{finding.category}</span>
                      </div>
                      <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: '#0A1628', fontWeight: 500 }}>{finding.message}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {criticalCount > 0 && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={createTasksFromFindings} disabled={creatingTasks} className="btn btn-primary">
                  <Plus size={14} /> {creatingTasks ? 'Creating tasks...' : `Create Tasks for ${criticalCount} Critical Finding${criticalCount > 1 ? 's' : ''}`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 768px) {
          .audit-score-row { grid-template-columns: 1fr !important; }
          .audit-stats-row { grid-template-columns: repeat(3, 1fr) !important; gap: 8px !important; }
          .audit-donut-card { grid-column: span 3 !important; }
          .audit-header { flex-direction: column !important; align-items: flex-start !important; }
          .audit-header-btns { flex-wrap: wrap !important; width: 100% !important; }
          .audit-header-btns .btn { flex: 1 !important; justify-content: center !important; font-size: 12px !important; }
        }
      `}</style>
      <style>{`
        @media (max-width: 768px) {
          .mobile-stack { flex-direction: column !important; }
          .mobile-full { width: 100% !important; min-width: 0 !important; }
          .mobile-hide { display: none !important; }
          .mobile-grid-1 { grid-template-columns: 1fr !important; }
          .mobile-grid-2 { grid-template-columns: repeat(2, 1fr) !important; }
          .mobile-p { padding: 12px 14px !important; }
          .mobile-font { font-size: 13px !important; }
        }
      `}</style>
    </div>
  );
}

export default Audit;
